import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { X } from "lucide-react";
import TechLogo from "./TechLogos";

// Right-side drawer used by the Tech section.
// Subtle, engineered feel — no neon, no decorative motion.
export default function TechDrawer({ open, onClose, item, labels }) {
  // Lock background scroll while open and close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const sections = item
    ? [
        { label: labels.powers, body: item.powers },
        { label: labels.whyWeUse, body: item.whyWeUse },
        { label: labels.alternatives, body: item.alternatives },
        { label: labels.founder, body: item.founder, accent: true },
        { label: labels.technical, body: item.technical },
      ]
    : [];

  return (
    <AnimatePresence>
      {open && item && (
        <motion.div
          className="fixed inset-0 z-[120]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          aria-modal="true"
          role="dialog"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label={labels.close}
            onClick={onClose}
            className="absolute inset-0 bg-black/55 backdrop-blur-[6px]"
          />

          {/* Panel */}
          <motion.aside
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-0 flex h-full w-full max-w-[560px] flex-col border-l border-white/[0.06] bg-[#0a0a0c] text-white shadow-[0_0_80px_rgba(0,0,0,0.6)]"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-6 border-b border-white/[0.06] px-8 py-7">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/[0.07] bg-white/[0.02]">
                  <TechLogo id={item.id} name={item.name} size={26} />
                </div>
                <div>
                  <div className="font-body text-[10px] tracking-[0.3em] uppercase text-white/40">
                    {labels.eyebrow}
                  </div>
                  <h3 className="font-display text-2xl tracking-tight text-white">
                    {item.name}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={labels.close}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/[0.08] text-white/65 transition hover:border-white/20 hover:text-white"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            {/* Tagline */}
            <div className="border-b border-white/[0.06] px-8 py-6">
              <p className="font-body text-base leading-relaxed text-white/75">
                {item.tagline}
              </p>
            </div>

            {/* Scrollable sections */}
            <div className="flex-1 overflow-y-auto px-8 py-7">
              <div className="flex flex-col gap-9">
                {sections.map((s) => (
                  <section key={s.label}>
                    <div className="mb-3 flex items-center gap-3">
                      <span className="h-px w-6 bg-white/25" />
                      <h4 className="font-body text-[10px] tracking-[0.32em] uppercase text-white/55">
                        {s.label}
                      </h4>
                    </div>
                    <p
                      className={
                        s.accent
                          ? "font-serif-italic text-[17px] leading-relaxed text-white/85"
                          : "font-body text-[15px] leading-relaxed text-white/70"
                      }
                    >
                      {s.body}
                    </p>
                  </section>
                ))}
              </div>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
